import React, { useRef, useState } from 'react'
import { Calendar, CalendarStep } from '../types/dimmingProfile.types'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'
import {
  levelForZenith,
  solarTimeForZenith,
  solarZenithDeg,
  sunHorizonTimes,
} from '../utils/solar'

interface CalendarCurveProps {
  calendar: Calendar
  strings: DimmingProfileUiStrings
  // When provided AND coordinates are available, the chart is editable (click to
  // add a step, drag a handle to adjust, double-click to remove). Otherwise the
  // chart stays a read-only preview.
  onChange?: (next: Calendar) => void
}

// Styling mirrors ProfileCurve.tsx so the two diagrams read as a pair.
const COLOR_AXIS = '#9ca3af'
const COLOR_GRID = '#eef2f7'
const COLOR_GRID_MAJOR = '#dde3ec'
const COLOR_CURVE = '#2563eb'
const COLOR_MARKER = '#f59e0b'

const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24]
const LEVEL_TICKS = [0, 25, 50, 75, 100]

// Plot geometry (self-contained; ProfileCurve gets its geometry from a presenter).
const WIDTH = 640
const HEIGHT = 240
const MARGIN_LEFT = 40
const MARGIN_RIGHT = 16
const MARGIN_TOP = 12
const MARGIN_BOTTOM = 36
const PLOT_WIDTH = WIDTH - MARGIN_LEFT - MARGIN_RIGHT
const PLOT_HEIGHT = HEIGHT - MARGIN_TOP - MARGIN_BOTTOM

// Vertical inset of the LEVEL scale inside the plot: 0% and 100% map a few px
// away from the plot border so handles/plateaus at the extremes stay visible
// (a 0% point used to sit exactly ON the bottom border). Mirrors
// CalendarStepsPresenter's LEVEL_INSET_* so the calendar charts read as a set.
const LEVEL_INSET_TOP = 8
const LEVEL_INSET_BOTTOM = 12
const LEVEL_SPAN = PLOT_HEIGHT - LEVEL_INSET_TOP - LEVEL_INSET_BOTTOM

// One sample per minute over the representative day (00:00 → 24:00).
const SAMPLES = 24 * 60

type StepsKey = 'sunrise_steps' | 'sunset_steps'

const timeToX = (tHours: number): number =>
  MARGIN_LEFT + (tHours / 24) * PLOT_WIDTH
const levelToY = (level: number): number =>
  MARGIN_TOP + LEVEL_INSET_TOP + (1 - level / 100) * LEVEL_SPAN

// Inverse of timeToX / levelToY, clamped to the plot domain.
const xToTime = (x: number): number =>
  Math.max(0, Math.min(24, ((x - MARGIN_LEFT) / PLOT_WIDTH) * 24))
const yToLevel = (y: number): number =>
  Math.max(
    0,
    Math.min(100, (1 - (y - MARGIN_TOP - LEVEL_INSET_TOP) / LEVEL_SPAN) * 100),
  )

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v))

// Sort a calendar step array ascending by zenith (immutably).
const sortByZenith = (steps: CalendarStep[]): CalendarStep[] =>
  [...steps].sort((a, b) => a.zenith_angle_deg - b.zenith_angle_deg)

// Editable intensity-vs-time view of the zenith calendar for a representative
// (equinox) day. With `onChange` + coordinates it is interactive; otherwise it
// is a read-only preview. See utils/solar.ts for the model's limits.
const CalendarCurve: React.FC<CalendarCurveProps> = ({
  calendar,
  strings,
  onChange,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  // The handle currently being dragged: which array + index within it.
  const [drag, setDrag] = useState<{ arrayKey: StepsKey; index: number } | null>(
    null,
  )

  // Compromis : le modèle ne porte pas de sentinelle null pour « non renseigné ».
  // On considère les coordonnées absentes UNIQUEMENT quand lat ET lng valent 0
  // (cas du calendrier fraîchement créé). Une latitude 0 seule (équateur) avec
  // une longitude non nulle reste une position valide. Voir revue MR !2.
  const hasCoords = !(
    calendar.latitude_deg === 0 && calendar.longitude_deg === 0
  )
  const lat = calendar.latitude_deg
  // Editing is enabled only with a change handler AND usable coordinates.
  const editable = !!onChange && hasCoords

  // Map a pointer event to plot coordinates {tHours, level} via the SVG's
  // inverse screen CTM (mirrors ProfileCurve's coordinate handling).
  const clientToPlot = (
    clientX: number,
    clientY: number,
  ): { tHours: number; level: number } | null => {
    const svg = svgRef.current
    if (!svg) return null
    const ctm = svg.getScreenCTM()
    if (!ctm) return null
    const pt = svg.createSVGPoint()
    pt.x = clientX
    pt.y = clientY
    const local = pt.matrixTransform(ctm.inverse())
    if (!Number.isFinite(local.x) || !Number.isFinite(local.y)) return null
    return { tHours: xToTime(local.x), level: yToLevel(local.y) }
  }

  // Sample the intensity over the day. Morning (t < 12) uses sunrise_steps as
  // the sun rises; afternoon/evening (t ≥ 12) uses sunset_steps as it sets.
  const points: Array<{ x: number; y: number }> = []
  for (let i = 0; i <= SAMPLES; i++) {
    const tHours = (i / SAMPLES) * 24
    const zenith = solarZenithDeg(lat, tHours)
    const steps =
      tHours < 12 ? calendar.sunrise_steps : calendar.sunset_steps
    const level = levelForZenith(zenith, steps)
    points.push({ x: timeToX(tHours), y: levelToY(level) })
  }

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ')

  const horizon = hasCoords ? sunHorizonTimes(lat) : null

  // ── editing handlers ──────────────────────────────────────────────────────

  // Background click: upsert a step at the clicked {tHours, level}.
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (!editable) return
    if ((e.target as Element).getAttribute('data-handle') === '1') return
    const p = clientToPlot(e.clientX, e.clientY)
    if (!p) return
    const zenith = Math.round(solarZenithDeg(lat, p.tHours))
    const lvl = clamp(Math.round(p.level), 0, 100)
    if (!Number.isFinite(zenith)) return
    const arrayKey: StepsKey =
      p.tHours < 12 ? 'sunrise_steps' : 'sunset_steps'
    const existing = calendar[arrayKey]
    const idx = existing.findIndex((s) => s.zenith_angle_deg === zenith)
    let next: CalendarStep[]
    if (idx >= 0) {
      next = existing.map((s, i) =>
        i === idx ? { ...s, dimming_level_percent: lvl } : s,
      )
    } else {
      next = [
        ...existing,
        { zenith_angle_deg: zenith, dimming_level_percent: lvl },
      ]
    }
    onChange?.({ ...calendar, [arrayKey]: sortByZenith(next) })
  }

  const beginDrag = (arrayKey: StepsKey, index: number, e: React.PointerEvent) => {
    if (!editable) return
    e.stopPropagation()
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    setDrag({ arrayKey, index })
  }

  const moveDrag = (e: React.PointerEvent) => {
    if (!drag || !editable) return
    const p = clientToPlot(e.clientX, e.clientY)
    if (!p) return
    // am handles stay in the morning half, pm handles in the evening half, so a
    // step's identity is stable even if dragged across noon.
    const isAm = drag.arrayKey === 'sunrise_steps'
    const tClamped = isAm
      ? clamp(p.tHours, 0.01, 11.99)
      : clamp(p.tHours, 12.01, 23.99)
    const zenith = clamp(Math.round(solarZenithDeg(lat, tClamped)), 1, 179)
    const lvl = clamp(Math.round(p.level), 0, 100)
    if (!Number.isFinite(zenith) || !Number.isFinite(lvl)) return
    const arr = calendar[drag.arrayKey]
    const updated = arr.map((s, i) =>
      i === drag.index
        ? { zenith_angle_deg: zenith, dimming_level_percent: lvl }
        : s,
    )
    onChange?.({ ...calendar, [drag.arrayKey]: sortByZenith(updated) })
  }

  const endDrag = (e: React.PointerEvent) => {
    if (!drag) return
    ;(e.target as Element).releasePointerCapture?.(e.pointerId)
    setDrag(null)
  }

  // Double-click a handle: remove that step.
  const deleteStep = (arrayKey: StepsKey, index: number, e: React.MouseEvent) => {
    if (!editable) return
    e.stopPropagation()
    const next = calendar[arrayKey].filter((_, i) => i !== index)
    onChange?.({ ...calendar, [arrayKey]: next })
  }

  // Build the step markers for one steps array, skipping any zenith that
  // can't be placed on this latitude (solarTimeForZenith → null). Markers are
  // ALWAYS drawn (read-only preview included) so every configured step —
  // including 0% ones — is visible; they are draggable only when editable.
  const renderHandles = (arrayKey: StepsKey, half: 'am' | 'pm') =>
    calendar[arrayKey].map((s, i) => {
      const t = solarTimeForZenith(lat, s.zenith_angle_deg, half)
      if (t === null || !Number.isFinite(t)) return null
      const cx = timeToX(t)
      const cy = levelToY(s.dimming_level_percent)
      return (
        <circle
          key={`${arrayKey}-${i}-${s.zenith_angle_deg}`}
          data-handle="1"
          cx={cx}
          cy={cy}
          r={editable ? 5.5 : 4}
          fill={COLOR_CURVE}
          stroke="#fff"
          strokeWidth={2}
          style={editable ? { cursor: 'grab' } : { pointerEvents: 'none' }}
          onPointerDown={editable ? (e) => beginDrag(arrayKey, i, e) : undefined}
          onPointerMove={editable ? moveDrag : undefined}
          onPointerUp={editable ? endDrag : undefined}
          onDoubleClick={editable ? (e) => deleteStep(arrayKey, i, e) : undefined}
        />
      )
    })

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{
          width: '100%',
          height: 'auto',
          touchAction: 'none',
          userSelect: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          cursor: editable ? 'crosshair' : 'default',
        }}
        onClick={handleBackgroundClick}
      >
        {/* Plot border */}
        <rect
          x={MARGIN_LEFT}
          y={MARGIN_TOP}
          width={PLOT_WIDTH}
          height={PLOT_HEIGHT}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1}
        />

        {/* Horizontal level grid + labels */}
        {LEVEL_TICKS.map((lvl) => {
          const y = levelToY(lvl)
          return (
            <g key={`lvl-${lvl}`}>
              <line
                x1={MARGIN_LEFT}
                y1={y}
                x2={MARGIN_LEFT + PLOT_WIDTH}
                y2={y}
                stroke={lvl === 0 ? COLOR_GRID_MAJOR : COLOR_GRID}
                strokeWidth={1}
              />
              <text
                x={MARGIN_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#6b7280"
              >
                {lvl}
              </text>
            </g>
          )
        })}

        {/* Vertical time grid + labels (every 3h) */}
        {HOUR_TICKS.map((h) => {
          const x = timeToX(h)
          return (
            <g key={`h-${h}`}>
              <line
                x1={x}
                y1={MARGIN_TOP}
                x2={x}
                y2={MARGIN_TOP + PLOT_HEIGHT}
                stroke={COLOR_GRID_MAJOR}
                strokeWidth={1}
              />
              <text
                x={x}
                y={MARGIN_TOP + PLOT_HEIGHT + 16}
                textAnchor="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {String(h).padStart(2, '0')}:00
              </text>
            </g>
          )
        })}

        {/* Sunrise / sunset vertical markers (θ crosses 90°). */}
        {horizon && (
          <>
            {([horizon.sunrise, horizon.sunset] as const).map((t, idx) => {
              const x = timeToX(t)
              return (
                <g key={`marker-${idx}`}>
                  <line
                    x1={x}
                    y1={MARGIN_TOP}
                    x2={x}
                    y2={MARGIN_TOP + PLOT_HEIGHT}
                    stroke={COLOR_MARKER}
                    strokeWidth={1}
                    strokeDasharray="4 3"
                  />
                  <text
                    x={x}
                    y={MARGIN_TOP + 12}
                    textAnchor="middle"
                    fontSize={10}
                    fill={COLOR_MARKER}
                  >
                    {idx === 0 ? strings.sunrise : strings.sunset}
                  </text>
                </g>
              )
            })}
          </>
        )}

        {/* Intensity curve (flat 0% when there are no steps). */}
        <path d={pathD} fill="none" stroke={COLOR_CURVE} strokeWidth={2} />

        {/* Step markers — always visible when coordinates allow placing them;
            draggable handles only when editable. */}
        {hasCoords && (
          <>
            {renderHandles('sunrise_steps', 'am')}
            {renderHandles('sunset_steps', 'pm')}
          </>
        )}

        {/* Inline hint when there are no coordinates: still draw the surface but
            tell the operator how to enable editing. */}
        {!hasCoords && (
          <text
            x={MARGIN_LEFT + PLOT_WIDTH / 2}
            y={MARGIN_TOP + PLOT_HEIGHT / 2}
            textAnchor="middle"
            fontSize={12}
            fill="#9ca3af"
          >
            {strings.calendarLinkToEdit}
          </text>
        )}

        {/* Axis captions */}
        <text
          x={MARGIN_LEFT + PLOT_WIDTH / 2}
          y={HEIGHT - 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
        >
          {strings.timeAxis}
        </text>
        <text
          x={12}
          y={MARGIN_TOP + PLOT_HEIGHT / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
          transform={`rotate(-90 12 ${MARGIN_TOP + PLOT_HEIGHT / 2})`}
        >
          {strings.levelAxis}
        </text>
      </svg>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {editable ? strings.calendarEditHint : strings.calendarCurveHint}
      </p>
    </div>
  )
}

export default CalendarCurve
